import React, { createContext, useContext, useEffect, useState } from 'react'

const ThemeContext = createContext({
    theme: 'system',
    isDark: false,
    setTheme: () => null,
    toggleTheme: () => null,
})

export const ThemeProvider = ({ children }) => {
    const [theme, setThemeState] = useState(() => {
        // Try to get theme from localStorage
        if (typeof window !== 'undefined') {
            return localStorage.getItem('theme') || 'system'
        }
        return 'system'
    })

    // Derived state for easier usage
    const [isDark, setIsDark] = useState(false)

    useEffect(() => {
        const root = window.document.documentElement

        const applyTheme = () => {
            // Remove both classes first
            root.classList.remove('light', 'dark')

            let effectiveTheme = theme

            if (theme === 'system') {
                const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
                    ? 'dark'
                    : 'light'
                effectiveTheme = systemTheme
            }

            root.classList.add(effectiveTheme)
            setIsDark(effectiveTheme === 'dark')
        }

        applyTheme()

        // Save to localStorage
        if (theme === 'system') {
            localStorage.removeItem('theme')
        } else {
            localStorage.setItem('theme', theme)
        }

        // Listen for system changes if theme is 'system'
        if (theme === 'system') {
            const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
            const handleChange = () => applyTheme()
            mediaQuery.addEventListener('change', handleChange)
            return () => mediaQuery.removeEventListener('change', handleChange)
        }
    }, [theme])

    const setTheme = (newTheme) => {
        setThemeState(newTheme)
    }

    const toggleTheme = () => {
        setThemeState(prev => {
            return isDark ? 'light' : 'dark'
        })
    }

    return (
        <ThemeContext.Provider value={{ theme, isDark, setTheme, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    )
}

export const useTheme = () => {
    const context = useContext(ThemeContext)
    if (context === undefined)
        throw new Error('useTheme must be used within a ThemeProvider')
    return context
}
